import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import IdeaCard     from '../../components/ideas/IdeaCard';
import AddIdeaModal from '../../components/ideas/AddIdeaModal';
import ApprovePanel from '../../components/ideas/ApprovePanel';
import FeedScan     from '../../components/ideas/FeedScan';
import { aiService } from '../../services/aiService';

export default function IdeaLab() {
  const { ideas, voiceState, anthropicKey, anthropicKeyReady, logActivity, currentUser, setBizTab, setActiveQueueId, addIdea } = useApp();
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [approvingId,   setApprovingId]   = useState(null);
  const [activeIdeaTab, setActiveIdeaTab] = useState('ideas');
  const [aiRunning,     setAiRunning]     = useState(false);
  const [feedRunning,   setFeedRunning]   = useState(false);

  const staffIdeas = ideas.filter(i => i.source === 'staff' && i.status !== 'archived' && i.status !== 'dismissed');
  const aiIdeas    = ideas.filter(i => i.source === 'ai'    && i.status !== 'archived' && i.status !== 'dismissed');
  const feedIdeas  = ideas.filter(i => i.source === 'feed'  && i.status !== 'archived' && i.status !== 'dismissed');

  async function runAISuggestions() {
    if (!anthropicKeyReady) { alert('Add your Anthropic key in Settings → API Keys first.'); return; }
    setAiRunning(true);
    try {
      const systemPrompt = [
        'You are a creative content strategist for Tayst Coffee, a B2B brand focused on hotels and hospitality.',
        voiceState?.description ? `Brand voice: ${voiceState.description}.` : '',
        voiceState?.audience    ? `Audience: ${voiceState.audience}.` : '',
      ].filter(Boolean).join(' ');

      const userPrompt = `Generate exactly 3 fresh content marketing ideas for Tayst Coffee.
Reply with a numbered list only — no intro, no explanation, no extra text.
Each item should be a concise, actionable idea title (max 10 words).

Format:
1. Idea title here
2. Idea title here
3. Idea title here`;

      const text = await aiService.rawComplete({
        system:       systemPrompt,
        message:      userPrompt,
        maxTokens:    256,
        anthropicKey,
      });

      const lines = text.split('\n').filter(l => /^\d+\./.test(l.trim())).slice(0, 3);
      if (lines.length === 0) throw new Error('No ideas returned. Please try again.');

      await Promise.all(lines.map(line =>
        addIdea({
          source:   'ai',
          title:    line.replace(/^\d+\.\s*/, '').trim(),
          notes:    '',
          category: 'other',
          urgency:  'anytime',
          status:   'new',
          ai_why:   'AI-generated content suggestion',
        })
      ));
      logActivity('AI generated suggestions', `${lines.length} ideas`);
    } catch (err) {
      alert('AI error: ' + err.message);
    } finally {
      setAiRunning(false);
    }
  }

  return (
    <div style={{ display:'flex', flex:1, overflow:'hidden', minHeight:0 }}>

      {/* Main pane */}
      <div className="main-pane" style={{ flex:1, overflowY:'auto' }}>
        <div style={{ display:'flex', gap:0, borderBottom:'2px solid var(--border)', marginBottom:16 }}>
          {[['ideas','💡 Ideas'],['feed','📡 Feed']].map(([id, label]) => (
            <button key={id} className={`mtab ${activeIdeaTab === id ? 'active' : ''}`}
              onClick={() => setActiveIdeaTab(id)}>{label}</button>
          ))}
        </div>

        {activeIdeaTab === 'feed' ? (
          <FeedScan feedRunning={feedRunning} setFeedRunning={setFeedRunning} />
        ) : (
          <div className="idea-lab-grid">
            {/* Staff ideas */}
            <div className="idea-section">
              <div className="idea-section-head">
                <span className="is-title">👥 Staff Ideas</span>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span className="is-count">{staffIdeas.length}</span>
                  <button className="btn btn-primary btn-xs" onClick={() => setShowAddModal(true)}>+ Add</button>
                </div>
              </div>
              <div className="idea-list">
                {staffIdeas.length === 0
                  ? <div className="empty-state">No ideas yet.<br/>Click + Add to submit one.</div>
                  : staffIdeas.map(idea => (
                    <IdeaCard key={idea.id} idea={idea}
                      onApprove={id => setApprovingId(id === approvingId ? null : id)}
                      approvingId={approvingId}
                      onGoToStudio={id => { setActiveQueueId(id); setBizTab('studio'); }} />
                  ))
                }
              </div>
            </div>

            {/* AI suggestions */}
            <div className="idea-section">
              <div className="idea-section-head">
                <span className="is-title">🤖 AI Suggestions</span>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span className="is-count">{aiIdeas.length}</span>
                  <button className="btn btn-outline btn-xs" onClick={runAISuggestions} disabled={aiRunning}>
                    {aiRunning ? <span className="dots"><i/><i/><i/></span> : '✨ Suggest'}
                  </button>
                </div>
              </div>
              <div className="idea-list">
                {aiIdeas.length === 0
                  ? <div className="empty-state">Click ✨ Suggest to get AI content ideas.</div>
                  : aiIdeas.map(idea => (
                    <IdeaCard key={idea.id} idea={idea}
                      onApprove={id => setApprovingId(id === approvingId ? null : id)}
                      approvingId={approvingId}
                      onGoToStudio={id => { setActiveQueueId(id); setBizTab('studio'); }} />
                  ))
                }
              </div>
            </div>

            {/* From feed */}
            <div className="idea-section">
              <div className="idea-section-head">
                <span className="is-title">📡 From Feed</span>
                <span className="is-count">{feedIdeas.length}</span>
              </div>
              <div className="idea-list">
                {feedIdeas.length === 0
                  ? <div className="empty-state">Ideas promoted from the LinkedIn feed appear here.</div>
                  : feedIdeas.map(idea => (
                    <IdeaCard key={idea.id} idea={idea}
                      onApprove={id => setApprovingId(id === approvingId ? null : id)}
                      approvingId={approvingId}
                      onGoToStudio={id => { setActiveQueueId(id); setBizTab('studio'); }} />
                  ))
                }
              </div>
            </div>
          </div>
        )}

        {approvingId && (
          <ApprovePanel ideaId={approvingId}
            onClose={() => setApprovingId(null)}
            onDone={() => { setApprovingId(null); setBizTab('studio'); }} />
        )}
      </div>

      {/* Right sidebar */}
      <div className="side-pane" style={{ padding:'14px 14px' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, fontWeight:700, marginBottom:12 }}>Hot Ideas</div>
        {ideas.filter(i => i.urgency === 'hot' && i.status === 'new').length === 0
          ? <div style={{ fontSize:11.5, color:'#9ca3af' }}>No hot ideas right now.</div>
          : ideas.filter(i => i.urgency === 'hot' && i.status === 'new').map(i => (
            <div key={i.id} style={{ padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
              <div style={{ fontWeight:700, marginBottom:2 }}>{i.title}</div>
              <div style={{ fontSize:10, color:'#9ca3af' }}>{i.category}</div>
            </div>
          ))
        }
        <div style={{ marginTop:18, paddingTop:14, borderTop:'1px solid var(--border)' }}>
          <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.09em', color:'#9ca3af', marginBottom:8 }}>
            By Status
          </div>
          {[['new','🔵','New'],['approved','🟢','Approved'],['generating','🟠','Generating'],['done','✅','Done']].map(([s, icon, label]) => (
            <div key={s} style={{ display:'flex', justifyContent:'space-between', fontSize:11.5, padding:'3px 0' }}>
              <span>{icon} {label}</span>
              <span style={{ fontWeight:700 }}>{ideas.filter(i => i.status === s).length}</span>
            </div>
          ))}
        </div>
      </div>

      {showAddModal && <AddIdeaModal onClose={() => setShowAddModal(false)} />}
    </div>
  );
}
